import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { InfoTooltip } from '@/components/ui/tooltip';
import { 
  TrendingUp, 
  TrendingDown, 
  AlertTriangle, 
  Package, 
  DollarSign, 
  ShoppingCart,
  BarChart3,
  RefreshCw,
  CheckCircle,
  Scale
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { usePageVisibility } from '@/hooks/usePageVisibility';

interface InventoryStats {
  totalMaterials: number;
  lowStockItems: number;
  outOfStockItems: number;
  totalValue: number;
  averageCost: number;
  totalTransactions: number;
}

interface Material {
  id: string;
  name: string;
  unit_type: string;
  current_stock: number;
  min_threshold: number;
  cost_per_unit: number;
  supplier?: string;
}



interface InventoryDashboardProps {
  selectedBranchId: string;
}

export const InventoryDashboard: React.FC<InventoryDashboardProps> = ({ selectedBranchId }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const isPageVisible = usePageVisibility();
  const [stats, setStats] = useState<InventoryStats>({
    totalMaterials: 0,
    lowStockItems: 0,
    outOfStockItems: 0,
    totalValue: 0,
    averageCost: 0,
    totalTransactions: 0
  });
  const [materials, setMaterials] = useState<Material[]>([]);
  const [loading, setLoading] = useState(true);
  const previousBranchIdRef = useRef<string | undefined>();
  const hasInitializedRef = useRef(false);

  const loadDashboardData = React.useCallback(async () => {
    if (!user) return;
    setLoading(true);

    try {
      // Load materials
      const { data: materialsData, error: materialsError } = await (supabase as any)
        .from('raw_materials')
        .select('*')
        .eq('user_id', user.id)
        .eq('branch_id', selectedBranchId)
        .order('created_at', { ascending: false });

      if (materialsError) {
        throw materialsError;
      }

      const materialsArray = (materialsData || []) as unknown as Material[];
      setMaterials(materialsArray);

      // Calculate stats
      const totalMaterials = materialsArray.length;
      const lowStockItems = materialsArray.filter((m: Material) => m.current_stock <= m.min_threshold).length;
      const outOfStockItems = materialsArray.filter((m: Material) => m.current_stock <= 0).length;
      const totalValue = materialsArray.reduce((sum: number, m: Material) => sum + (m.current_stock * m.cost_per_unit), 0);
      const averageCost = totalMaterials > 0 ? totalValue / totalMaterials : 0;

      setStats({
        totalMaterials,
        lowStockItems,
        outOfStockItems,
        totalValue,
        averageCost,
        totalTransactions: 0
      });
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      toast({
        title: 'Error loading dashboard',
        description: 'Failed to load inventory data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [user, selectedBranchId, toast]);

  useEffect(() => {
    if (user && selectedBranchId && isPageVisible) {
      const branchChanged = previousBranchIdRef.current !== selectedBranchId;
      if (!hasInitializedRef.current || branchChanged) {
        loadDashboardData();
        previousBranchIdRef.current = selectedBranchId;
        hasInitializedRef.current = true;
      }
    }
  }, [user, selectedBranchId, isPageVisible, loadDashboardData]);

  const formatCurrency = (amount: number, currency: string = 'EGP') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const getStockPercentage = (material: Material) => {
    if (material.min_threshold === 0) return 100;
    return Math.min((material.current_stock / material.min_threshold) * 100, 100);
  };

  const getStockStatus = (material: Material) => {
    if (material.current_stock <= 0) return 'out-of-stock';
    if (material.current_stock <= material.min_threshold) return 'low-stock';
    return 'in-stock';
  };

  if (loading) {
    return (
      <div className="space-y-4 md:space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3 lg:gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="border-0 md:border shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 p-3 md:p-4 lg:p-6">
                <div className="h-3 md:h-4 w-16 md:w-20 bg-gray-200 rounded animate-pulse" />
                <div className="h-3 md:h-4 w-3 md:w-4 bg-gray-200 rounded animate-pulse" />
              </CardHeader>
              <CardContent className="p-3 md:p-4 lg:p-6 pt-0">
                <div className="h-6 md:h-8 w-12 md:w-16 bg-gray-200 rounded animate-pulse mb-1 md:mb-2" />
                <div className="h-2 md:h-3 w-20 md:w-24 bg-gray-200 rounded animate-pulse" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3 lg:gap-4">
        <Card className="border-0 md:border shadow-sm overflow-hidden">
          <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-1 px-3 md:px-4 py-3">
            <div className="flex items-start gap-1 min-w-0 flex-1">
              <CardTitle className="text-xs font-medium text-left text-gray-600 flex-1">
                Materials
              </CardTitle>
              <InfoTooltip
                title="Total Materials"
                description="The total number of different materials currently in your inventory."
                calculation="Count of all materials with current_stock > 0"
              />
            </div>
            <Package className="h-4 w-4 text-gray-400 flex-shrink-0" />
          </CardHeader>
          <CardContent className="px-3 md:px-4 py-2">
            <div className="text-xl font-bold text-gray-900">{stats.totalMaterials}</div>
            <p className="text-xs text-gray-600 mt-0.5">
              Active items
            </p>
          </CardContent>
        </Card>

        <Card className="border-0 md:border shadow-sm overflow-hidden">
          <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-1 px-3 md:px-4 py-3">
            <div className="flex items-start gap-1 min-w-0 flex-1">
              <CardTitle className="text-xs font-medium text-left text-gray-600 flex-1">
                Total Value
              </CardTitle>
              <InfoTooltip
                title="Total Inventory Value"
                description="The total monetary value of all materials currently in stock."
                calculation="Sum of (current_stock × cost_per_unit) for all materials"
              />
            </div>
            <DollarSign className="h-4 w-4 text-gray-400 flex-shrink-0" />
          </CardHeader>
          <CardContent className="px-3 md:px-4 py-2">
            <div className="text-lg font-bold text-gray-900 break-words">{formatCurrency(stats.totalValue, 'EGP')}</div>
            <p className="text-xs text-gray-600 mt-0.5">
              Current value
            </p>
          </CardContent>
        </Card>

        <Card className="border-0 md:border shadow-sm overflow-hidden">
          <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-1 px-3 md:px-4 py-3">
            <div className="flex items-start gap-1 min-w-0 flex-1">
              <CardTitle className="text-xs font-medium text-left text-gray-600 flex-1">
                Low Stock
              </CardTitle>
              <InfoTooltip
                title="Low Stock Items"
                description="Materials that are running low and need to be restocked soon."
                calculation="Count of materials where current_stock ≤ min_threshold AND current_stock > 0"
              />
            </div>
            <AlertTriangle className="h-4 w-4 text-orange-500 flex-shrink-0" />
          </CardHeader>
          <CardContent className="px-3 md:px-4 py-2">
            <div className="text-xl font-bold text-orange-600">{stats.lowStockItems}</div>
            <p className="text-xs text-gray-600 mt-0.5">
              Below min
            </p>
          </CardContent>
        </Card>

        <Card className="border-0 md:border shadow-sm overflow-hidden">
          <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-1 px-3 md:px-4 py-3">
            <div className="flex items-start gap-1 min-w-0 flex-1">
              <CardTitle className="text-xs font-medium text-left text-gray-600 flex-1">
                Out of Stock
              </CardTitle>
              <InfoTooltip
                title="Out of Stock Items"
                description="Materials that have completely run out and need immediate restocking."
                calculation="Count of materials where current_stock ≤ 0"
              />
            </div>
            <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0" />
          </CardHeader>
          <CardContent className="px-3 md:px-4 py-2">
            <div className="text-xl font-bold text-red-600">{stats.outOfStockItems}</div>
            <p className="text-xs text-gray-600 mt-0.5">
              Zero stock
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Alerts */}
      {(stats.outOfStockItems > 0 || stats.lowStockItems > 0) && (
        <Alert className="border-0 md:border">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          <AlertDescription className="text-sm">
            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
              <span className="font-medium">Inventory Alert:</span>
              <div className="flex flex-wrap items-center gap-1">
                {stats.outOfStockItems > 0 && (
                  <span className="text-red-600 font-medium text-xs sm:text-sm">
                    {stats.outOfStockItems} item{stats.outOfStockItems > 1 ? 's' : ''} out of stock
                  </span>
                )}
                {stats.lowStockItems > 0 && (
                  <>
                    {stats.outOfStockItems > 0 && <span className="text-muted-foreground">•</span>}
                    <span className="text-orange-600 font-medium text-xs sm:text-sm">
                      {stats.lowStockItems} item{stats.lowStockItems > 1 ? 's' : ''} running low
                    </span>
                  </>
                )}
              </div>
            </div>
            <p className="text-xs mt-1 text-muted-foreground">Please review and restock as needed.</p>
          </AlertDescription>
        </Alert>
      )}

      {/* Stock Levels and Recent Transactions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-4 lg:gap-6">
        {/* Stock Levels */}
        <Card className="border-0 md:border shadow-sm">
          <CardHeader className="p-3 md:p-4 lg:p-6">
            <CardTitle className="flex items-center gap-2 text-base md:text-lg">
              <BarChart3 className="h-4 w-4 md:h-5 md:w-5" />
              Stock Levels
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 md:space-y-4 p-3 md:p-4 lg:p-6 pt-0">
            {materials.slice(0, 8).map((material) => {
              const percentage = getStockPercentage(material);
              const status = getStockStatus(material);
              
              return (
                <div key={material.id} className="space-y-1.5 md:space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs md:text-sm font-medium truncate flex-1">{material.name}</span>
                    <div className="flex items-center gap-1.5 md:gap-2 flex-shrink-0">
                      <span className="text-[10px] md:text-xs text-gray-600 whitespace-nowrap">
                        {material.current_stock} {material.unit_type}
                      </span>
                      <Badge 
                        variant={status === 'out-of-stock' ? 'destructive' : 
                               status === 'low-stock' ? 'secondary' : 'default'}
                        className="text-[10px] md:text-xs px-1.5 md:px-2 py-0 md:py-0.5"
                      >
                        {status === 'out-of-stock' ? 'Out' :
                         status === 'low-stock' ? 'Low' : 'OK'}
                      </Badge>
                    </div>
                  </div>
                  <Progress value={percentage} className="h-1.5 md:h-2" />
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Low Stock Alerts */}
        <Card className="border-0 md:border shadow-sm">
          <CardHeader className="p-3 md:p-4 lg:p-6">
            <CardTitle className="flex items-center gap-2 text-base md:text-lg">
              <AlertTriangle className="h-4 w-4 md:h-5 md:w-5" />
              Low Stock Alerts
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 md:space-y-3 p-3 md:p-4 lg:p-6 pt-0">
            {materials.filter(m => m.current_stock <= m.min_threshold).length > 0 ? (
              materials
                .filter(m => m.current_stock <= m.min_threshold)
                .slice(0, 5)
                .map((material) => (
                  <div key={material.id} className="flex items-start sm:items-center justify-between gap-2 p-2 md:p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-xs md:text-sm text-red-800 truncate">{material.name}</div>
                      <div className="text-[10px] md:text-xs text-red-600 truncate">
                        Current: {material.current_stock} {material.unit_type} • Min: {material.min_threshold} {material.unit_type}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <Badge variant="destructive" className="text-[10px] md:text-xs px-1.5 md:px-2 whitespace-nowrap">
                        {material.current_stock <= 0 ? 'Out' : 'Low'}
                      </Badge>
                      <div className="text-[10px] md:text-xs text-red-600 mt-0.5 md:mt-1 whitespace-nowrap">
                        {formatCurrency(material.cost_per_unit, 'EGP')}/{material.unit_type}
                      </div>
                    </div>
                  </div>
                ))
            ) : (
              <div className="text-center py-6 md:py-8">
                <CheckCircle className="h-10 w-10 md:h-12 md:w-12 text-green-400 mx-auto mb-3 md:mb-4" />
                <p className="text-xs md:text-sm text-gray-500 mb-1 md:mb-2">All items well stocked</p>
                <p className="text-[10px] md:text-xs text-gray-400">
                  No low stock alerts at the moment
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}; 